/**
 * Coordinate projection utilities for the concave globe.
 *
 * Provides conversions between:
 * - Geographic (lat/lng)
 * - Spherical (theta/phi/radius)
 * - Cartesian (x/y/z)
 * - Planar (for flat mode)
 */

import { toRadians, toDegrees } from '@/domain/geo'
import { GLOBE_CONFIG } from '../constants'
import type { SphericalCoordinate } from '../types'

/**
 * Convert latitude/longitude to spherical coordinates.
 *
 * Uses Three.js spherical convention:
 * - theta: azimuthal angle (longitude), 0 at +X axis, increases counterclockwise
 * - phi: polar angle from +Y axis, 0 at top, PI/2 at equator
 *
 * @param latitude - Latitude in degrees (-85 to 85)
 * @param longitude - Longitude in degrees (-180 to 180)
 * @param radius - Sphere radius (defaults to GLOBE_CONFIG.SPHERE_RADIUS)
 */
export function latLngToSpherical(
  latitude: number,
  longitude: number,
  radius: number = GLOBE_CONFIG.SPHERE_RADIUS
): SphericalCoordinate {
  // Clamp latitude to Web Mercator limits
  const clampedLat = Math.max(
    GLOBE_CONFIG.MIN_LATITUDE,
    Math.min(GLOBE_CONFIG.MAX_LATITUDE, latitude)
  )

  // Convert to radians
  // theta = longitude (adjusted so 0 lng = 0 theta)
  const theta = toRadians(longitude)

  // phi = colatitude (0 at north pole, PI/2 at equator, PI at south pole)
  // For hemisphere, we only use 0 to PI/2 (north pole to equator)
  const phi = toRadians(90 - clampedLat)

  return { theta, phi, radius }
}

/**
 * Convert spherical coordinates back to latitude/longitude.
 *
 * @param coord - Spherical coordinate
 * @returns Geographic position
 */
export function sphericalToLatLng(coord: SphericalCoordinate): {
  latitude: number
  longitude: number
} {
  const latitude = 90 - toDegrees(coord.phi)
  const longitude = toDegrees(coord.theta)

  return { latitude, longitude }
}

/**
 * Convert spherical coordinates to Cartesian (x, y, z).
 *
 * Uses Three.js coordinate system:
 * - Y is up
 * - At phi=0 (north pole), point is at (0, radius, 0)
 * - At phi=PI/2, theta=0, point is at (radius, 0, 0)
 *
 * @param coord - Spherical coordinate
 * @returns Cartesian [x, y, z] tuple
 */
export function sphericalToCartesian(
  coord: SphericalCoordinate
): [number, number, number] {
  const { theta, phi, radius } = coord

  const x = radius * Math.sin(phi) * Math.cos(theta)
  const y = radius * Math.cos(phi)
  const z = radius * Math.sin(phi) * Math.sin(theta)

  return [x, y, z]
}

/**
 * Convert Cartesian coordinates to spherical.
 *
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param z - Z coordinate
 * @returns Spherical coordinate
 */
export function cartesianToSpherical(
  x: number,
  y: number,
  z: number
): SphericalCoordinate {
  const radius = Math.sqrt(x * x + y * y + z * z)

  if (radius === 0) {
    return { theta: 0, phi: 0, radius: 0 }
  }

  const phi = Math.acos(y / radius)
  const theta = Math.atan2(z, x)

  return { theta, phi, radius }
}

/**
 * Convert latitude/longitude directly to Cartesian on the sphere.
 *
 * Convenience function combining latLngToSpherical and sphericalToCartesian.
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @param radius - Sphere radius
 * @returns Cartesian [x, y, z] tuple
 */
export function latLngToCartesian(
  latitude: number,
  longitude: number,
  radius: number = GLOBE_CONFIG.SPHERE_RADIUS
): [number, number, number] {
  const spherical = latLngToSpherical(latitude, longitude, radius)
  return sphericalToCartesian(spherical)
}

/**
 * Convert Cartesian coordinates to latitude/longitude.
 *
 * Convenience function combining cartesianToSpherical and sphericalToLatLng.
 *
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param z - Z coordinate
 * @returns Geographic position
 */
export function cartesianToLatLng(
  x: number,
  y: number,
  z: number
): { latitude: number; longitude: number } {
  const spherical = cartesianToSpherical(x, y, z)
  return sphericalToLatLng(spherical)
}

/**
 * Convert latitude/longitude to a flat plane position.
 *
 * Used when curvature = 0 (flat mode).
 * Uses equirectangular projection centered at origin.
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @param scale - Scale factor for the plane size
 * @returns Cartesian [x, y, z] tuple (y is always 0 for flat plane)
 */
export function latLngToPlane(
  latitude: number,
  longitude: number,
  scale: number = GLOBE_CONFIG.SPHERE_RADIUS
): [number, number, number] {
  // Normalize longitude to 0-1 range
  const u = (longitude + 180) / 360

  // Normalize latitude to 0-1 range (inverted so north is at lower v)
  const v = (90 - latitude) / 180

  // Map to plane coordinates centered at origin
  const x = (u - 0.5) * scale * 2
  const y = 0 // Flat plane in XZ
  const z = (v - 0.5) * scale * 2

  return [x, y, z]
}

/**
 * Interpolate between spherical and planar positions based on curvature.
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @param curvature - 0 = flat, 1 = full sphere
 * @param radius - Sphere radius
 * @returns Interpolated [x, y, z] position
 */
export function latLngToMorphedPosition(
  latitude: number,
  longitude: number,
  curvature: number,
  radius: number = GLOBE_CONFIG.SPHERE_RADIUS
): [number, number, number] {
  const spherePos = latLngToCartesian(latitude, longitude, radius)
  const planePos = latLngToPlane(latitude, longitude, radius)

  // Linear interpolation between sphere and plane positions
  return [
    spherePos[0] * curvature + planePos[0] * (1 - curvature),
    spherePos[1] * curvature + planePos[1] * (1 - curvature),
    spherePos[2] * curvature + planePos[2] * (1 - curvature),
  ]
}
